import { ReactElement } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'next-i18next';
import { useWindowSize } from '@Hotels-at-Home/hah-fe-core/dist/hooks';
import { MD_WIDTH } from '@Hotels-at-Home/hah-fe-core/dist/constants';
import Swiper from '../Swiper/Swiper';

interface ServiceItem {
  id: number;
  icon: string;
  translationKey: string;
  className?: string;
}

interface ServicesProps {
  items: ServiceItem[];
  border?: boolean;
  mainSection?: boolean;
}

const Services = ({ items, border = true, mainSection = false }: ServicesProps): ReactElement => {
  const { t: tTitle, ready } = useTranslation('', { keyPrefix: '$local.$services.$titles' });
  const { t: tDescription } = useTranslation('', { keyPrefix: '$local.$services.$descriptions' });
  const { width } = useWindowSize();
  // Guard on width before comparing. useWindowSize currently starts at
  // `undefined` (core 8.19.1), so `undefined <= MD_WIDTH` is already false and
  // the server renders the list. But core has a queued change that starts it at
  // 0 -- and `0 <= MD_WIDTH` is TRUE, which would make the server render the
  // carousel and hand DESKTOP the hydration swap (and the layout shift) instead.
  // NOV/MVP/IBIS already guard this way; MER was the only site that did not.
  const isMobile = width && width <= MD_WIDTH;

  if (!ready) {
    return null;
  }

  const renderServiceItem = ({ icon, translationKey, className }: ServiceItem) => (
    <div className={classnames('services-service', className)}>
      <i className={classnames('services-service-icon', icon)} />
      <div className="services-service-copy">
        <p className="services-service-title">
          <Trans
            i18nKey={translationKey}
            t={tTitle}
            components={{
              sup: <sup />,
            }}
          />
        </p>
        <p className="services-service-description">
          <Trans
            i18nKey={translationKey}
            t={tDescription}
            components={{
              sup: <sup />,
            }}
          />
        </p>
      </div>
    </div>
  );

  return (
    <section
      className={classnames(
        'services',
        mainSection ? 'content-services' : 'hp-content-wrapper',
        !border && 'no-border',
        isMobile && 'services-carousel',
      )}
    >
      {isMobile ? (
        <Swiper
          slides={items}
          renderSlide={renderServiceItem}
          navigation
          arrowBorder
          loop
          slidesPerView={1}
          spaceBetween={0}
          className="services-swiper"
          slideClassName={() => 'services-swiper-slide'}
        />
      ) : (
        <ul>
          {items.map(item => (
            <li key={item.id}>{renderServiceItem(item)}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default Services;
